/**
 * <Card> — the generic surface shell (molecule, L0.1).
 *
 * The single container behind the parent-home cards (`LiveSessionCard`, `RevisionProgramCard`, …):
 * a white surface with an optional 4px colored left rail. It is pressable only when `pressable`
 * is set (with an `onPress`, or `disabled`) — otherwise it renders a plain box.
 *
 * `leftBorderColor` is a `SemanticColorKey` token (no-hex rule); the caller can pass extra `style`
 * to override the shell (dimmed background, opacity, zero padding, …) — it is merged last.
 * `background` swaps the surface token (default `surface-raised`) so tinted cards (e.g. an alert)
 * reuse this shell instead of hand-rolling one.
 *
 * **Pas de filet, une seule ombre** (Brand core V13) : la carte n'a plus de contour, et c'est
 * l'ombre `0 6px 18px rgba(15,27,61,.06)` qui lui donne son bord sur la page. Elle est donc le
 * DÉFAUT — une carte sans ombre ni filet sur `#F5F6F8` n'aurait plus aucune limite.
 * `elevation="none"` reste pour une surface ENCASTRÉE : une ligne d'option d'une liste
 * déroulante, un bloc teinté posé à l'intérieur d'une autre carte.
 *
 * Sur la lavande des encarts ou sur une tinte, la carte dit à ce qu'elle porte qu'il est dans un encart
 * (`hooks/useOnInset`) ; sur tout autre fond, qu'il est sur une carte. Elle le lit sur sa prop
 * `background` : une carte repeinte par `style` ne le dit pas.
 */
import React from "react"
import { Pressable, View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { InsetContext } from "../../hooks/useOnInset"
import { isInsetSurface, type SemanticColorKey } from "../../theme/tokens/colors"
import { type ElevationKey } from "../../theme/tokens/elevation"
import { SpacingKey } from "../../theme"
import { shadowStyle } from "../../utils/shadow"

export type CardProps = {
    /** Renders a `<Pressable>` (with `onPress`) instead of a plain `<View>`. Defaults to false. */
    pressable?: boolean
    /** Tap handler — wired only when `pressable`. */
    onPress?: () => void
    /**
     * Pressable mais inerte : la carte reste un bouton pour un lecteur d'écran, annoncé désactivé,
     * estompée comme les autres contrôles du DS, et ne répond plus — avec ou sans `onPress`.
     * Défaut `false`.
     */
    disabled?: boolean
    /** Semantic color token for the 4px left rail. Omitted → no rail. */
    leftBorderColor?: SemanticColorKey
    /** Surface color token — defaults to `surface-raised`. */
    background?: SemanticColorKey
    /** `"card"` par défaut (l'ombre du livre) ; `"none"` pour une surface encastrée. */
    elevation?: ElevationKey
    p?: SpacingKey
    children: React.ReactNode
    /** Override slot — merged after the base shell (dimmed bg, opacity, padding, …). */
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function Card(props: CardProps) {
    const {
        pressable = false,
        onPress,
        disabled = false,
        leftBorderColor,
        background = "surface-raised",
        elevation = "card",
        p,
        children,
        style,
        testID,
    } = props

    const shell = [
        styles.card(leftBorderColor, background, elevation, p),
        disabled ? styles.disabled : undefined,
        style,
    ]
    const content = <InsetContext.Provider value={isInsetSurface(background)}>{children}</InsetContext.Provider>

    if (pressable && (onPress != null || disabled)) {
        return (
            <Pressable
                testID={testID}
                onPress={onPress}
                disabled={disabled}
                accessibilityRole="button"
                accessibilityState={{ disabled }}
                style={shell}
            >
                {content}
            </Pressable>
        )
    }

    return (
        <View testID={testID} style={shell}>
            {content}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    card: (
        leftBorderColor: SemanticColorKey | undefined,
        background: SemanticColorKey,
        elevation: ElevationKey,
        p?: SpacingKey
    ) => ({
        backgroundColor: theme.colors[background],
        borderRadius: theme.radius.lg,
        ...(leftBorderColor != null ? { borderLeftWidth: 4, borderLeftColor: theme.colors[leftBorderColor] } : null),
        padding: p ? theme.spacing[p] : theme.spacing["3"],
        ...shadowStyle(theme, elevation),
    }),
    // L'estompe d'un contrôle désactivé, celle de `<Button>` et `<IconButton>`.
    disabled: {
        opacity: 0.5,
    },
}))
